import React, { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { ShieldCheck, Mail, Chrome, Smartphone } from "lucide-react";
import { VerifyBar } from "../VerifyBar";
import { VerdictCard } from "./VerdictCard";
import { VerifyAdmin } from "./VerifyAdmin";
import { verifyJob } from "../../lib/api";

const useManifest = () => useEffect(() => {
  const l = document.createElement("link");
  l.rel = "manifest"; l.href = "/verify-manifest.webmanifest";
  document.head.appendChild(l);
  if ("serviceWorker" in navigator) navigator.serviceWorker.register("/verify-sw.js", { scope: "/verify" }).catch(() => {});
  document.title = "Verify Bar · A V Tech";
  return () => l.remove();
}, []);

const Result = ({ id }) => {
  const [j, setJ] = useState(null);
  useEffect(() => {
    let dead = false;
    const pull = () => verifyJob(id).then((r) => { if (dead) return; setJ(r); if (r.status !== "done") setTimeout(pull, 3000); }).catch(() => setJ({ status: "missing" }));
    pull();
    return () => { dead = true; };
  }, [id]);
  if (!j) return <p className="text-cyan-200/60">Loading…</p>;
  if (j.status === "missing") return <p className="text-rose-300" data-testid="verify-result-missing">Result not found.</p>;
  if (j.status !== "done") return <p className="text-cyan-100" data-testid="verify-result-checking">Checking, one minute…</p>;
  return <VerdictCard jobId={id} v={j.verdict} onAgain={() => { window.location.href = "/verify"; }} />;
};

const Ways = () => (
  <div className="grid sm:grid-cols-3 gap-3" data-testid="verify-ways">
    {[[Mail, "Email", "Forward anything to verify@avtechai.org — the verdict comes back in under a minute."],
      [Chrome, "Chrome", "Right-click any text, image, link or video → Check with Verify Bar."],
      [Smartphone, "Phone", "Add this page to your home screen, then Share → Verify Bar from any app."]].map(([I, t, d]) => (
      <div key={t} className="rounded-xl border border-cyan-500/20 p-3 bg-white/[0.02]"><I className="w-5 h-5 text-[#00F0FF]" /><p className="font-bold text-cyan-50 mt-1">{t}</p><p className="text-sm text-cyan-100/70">{d}</p></div>
    ))}
  </div>
);

export default function VerifyApp() {
  useManifest();
  const p = window.location.pathname.replace(/\/$/, "");
  const rid = (p.match(/^\/verify\/r\/([a-f0-9]+)$/) || [])[1];
  const qs = new URLSearchParams(window.location.search);
  const invite = qs.get("invite");
  if (qs.get("k")) { try { localStorage.setItem("verify_key", qs.get("k")); } catch (e) { /* storage blocked */ } }
  return (
    <div className="min-h-screen bg-[#030712] text-cyan-50 px-4 py-8" data-testid="verify-page">
      <Toaster position="top-center" richColors />
      {p === "/verify/admin" ? <VerifyAdmin /> : (
        <div className="max-w-2xl mx-auto space-y-6">
          <header className="space-y-2">
            <p className="flex items-center gap-2 text-[#00F0FF] font-black tracking-widest text-sm"><ShieldCheck className="w-5 h-5" /> VERIFY BAR · A V TECH</p>
            <h1 className="text-4xl sm:text-5xl font-black leading-tight">Is it real, or is it AI?</h1>
            <p className="text-cyan-100/70">A family checker for forwarded messages, photos, voice notes, videos, emails and letters. Green, amber or red — always "likely", never proof.</p>
            {invite && <p className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 text-amber-100 text-sm" data-testid="verify-invite-note">You've used your free check. Ask Sohel to add you to the family circle for unlimited checks.</p>}
          </header>
          {rid ? <Result id={rid} /> : <VerifyBar />}
          <Ways />
          <p className="text-xs text-cyan-200/40">No training on family data. Uploads are deleted within 24 hours unless kept as evidence. <a href="/verify-bar-extension.zip" className="underline" data-testid="verify-extension-download">Chrome extension</a></p>
        </div>
      )}
    </div>
  );
}
